import formatPayoutDueDate from './formatPayoutDueDate';
import getBOAAccountStatusCallback from './getBOAAccountStatusCallback';
import getHWAccountStatusCallback from './getHWAccountStatusCallback';
import isAlertVisible from './isAlertVisible';
import {Alerts} from '../../../reducers/alerts.reducer';
import {dateFormatUTC} from '../../../../shared/utils/DateUtils';
import {initConsent, initCreateTaxProfile, initOfflineType} from '../../../utils/PayoutUtils';
import {AlertComponentType, GetAlertProps, AlertItemProps} from '../types';

import {
    extractCurrentEarningsDeadlineInDays,
    getAccountClosedDate,
    getAgreementSystem
} from '../../../../shared/utils/AffiliateUtils';

export default function(type, props?: GetAlertProps): AlertItemProps {
    if (!isAlertVisible(type, AlertComponentType.HIDE_WARNING_BAR)) return null;

    const {affiliateAccount, configState, taxState, history} = props || {};

    switch (type) {
        case Alerts.GOOD_STANDING: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.common.no_action'
                    },
                    {
                        code: 'epic.affiliate.common.good_standing'
                    }
                ],
                closeable: true,
                severity: 'success'
            };
        }
        case Alerts.PENDING_DELETION: {
            const {statusEffectiveDate} = affiliateAccount || {};
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.common.tos.breached.v2',
                        args: [dateFormatUTC(statusEffectiveDate)]
                    }
                ],
                severity: 'error'
            };
        }
        case Alerts.RESET_EARNINGS: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.payout.threshold.expiry.reached.v2'
                    }
                ],
                severity: 'error'
            };
        }
        case Alerts.RESET_EARNINGS_WARNING: {
            const {earningsInfo} = affiliateAccount || {};
            const remainingDays = extractCurrentEarningsDeadlineInDays(earningsInfo);
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.payout.threshold.expiry.warning.v2',
                        args: [remainingDays]
                    }
                ]
            };
        }
        case Alerts.FN_CREATIVE_CCU: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.fortnite_games.warning.ccu_incorrect'
                    }
                ]
            };
        }
        case Alerts.INELIGIBLE_SOCIAL: {
            const {oauthViolation} = affiliateAccount || {};
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.social.channels.ineligible.warning.v2',
                        args: [
                            'overview#eligible',
                            oauthViolation && dateFormatUTC(oauthViolation.fixDueDate),
                            'settings'
                        ]
                    }
                ],
                cta: {
                    link: '/settings',
                    code: 'epic.affiliate.social.channels.update.v2'
                },
                AlertPopupProps: true,
                severity: 'error'
            };
        }
        case Alerts.MIGRATE_PROFILE: {
            const {whitelist = {}} = affiliateAccount || {};
            const system = getAgreementSystem(affiliateAccount);
            const migrateBy = `<strong>${dateFormatUTC(system.migrateBy)}</strong>`;
            const releasedAt = `<strong>${dateFormatUTC(system.releasedAt)}</strong>`;

            return {
                msgCodes: [
                    whitelist.isCreative && {
                        code: 'epic.affiliate.migration.tos.accept.fnCreative.v2',
                        args: [migrateBy]
                    },
                    {
                        code: 'epic.affiliate.common.tos.accept.v2',
                        args: [releasedAt, migrateBy]
                    }
                ],
                cta: {
                    link: '/new-terms',
                    code: 'epic.affiliate.tos.reviewAgreement.v2'
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.tos.updateRequireAccept.v2'
                },
                severity: 'error'
            };
        }
        case Alerts.CREATE_TAX_PROFILE: {
            return {
                msgCodes: [
                    {
                        code: `epic.affiliate.settings.tax.profile.payable`,
                        args: ['/settings']
                    }
                ]
            };
        }
        case 'tax.incompleted': {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payable.v2',
                        args: ['settings?createTaxProfile=true']
                    }
                ]
            };
        }
        case 'tax.pending': {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.pending.v2',
                        args: ['settings']
                    }
                ]
            };
        }
        case 'tax.consent': {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.consent.v2',
                        args: ['settings']
                    }
                ]
            };
        }
        case Alerts.ACCOUNT_NOT_EXIST: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.not.exist.v2'
                    }
                ]
            };
        }
        case Alerts.ACCOUNT_PENDING_ACTIVATION: {
            const {hyperWalletData} = taxState || {};
            const {link} = hyperWalletData || {};
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.pending.activation.v2',
                        args: [formatPayoutDueDate(affiliateAccount), link]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.active.wallet.v2',
                    onClick: getHWAccountStatusCallback(props)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.ACCOUNT_PENDING_VERIFICATION: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.pending.verification.v2',
                        args: [formatPayoutDueDate(affiliateAccount)]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.active.wallet.v2',
                    onClick: getHWAccountStatusCallback(props)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.ACCOUNT_UNDER_REVIEW: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.under.review.v2',
                        args: [configState.hyperWalletHomepage]
                    }
                ]
            };
        }
        case Alerts.ACCOUNT_PENDING_TRANSFER_METHOD: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.account.status.transfer',
                        args: [configState.hyperWalletHomepage]
                    }
                ]
            };
        }
        case Alerts.ACCOUNT_DISABLED: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.disabled.v2',
                        args: [
                            formatPayoutDueDate(affiliateAccount),
                            'settings?createTaxProfile=true'
                        ]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.retake.interview.v2',
                    onClick: () => initCreateTaxProfile(history)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.ACCOUNT_LOCKED: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.locked.v2',
                        args: [
                            formatPayoutDueDate(affiliateAccount),
                            configState.hyperwalletContactPage
                        ]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.contact.wallet.v2',
                    link: configState.hyperwalletContactPage,
                    isExternal: true
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.ACCOUNT_UNKNOWN_STATUS:
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.unknown.status.v2',
                        args: [
                            formatPayoutDueDate(affiliateAccount),
                            'settings?createTaxProfile=true'
                        ]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.retake.interview.v2',
                    onClick: () => initCreateTaxProfile(history)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        case Alerts.ACCOUNT_PENDING_REPROCESS: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.reprocess.v2'
                    }
                ],
                cta: {
                    code: 'epic.affiliate.social.channels.check.v2',
                    onClick: getHWAccountStatusCallback(props)
                }
            };
        }
        case Alerts.TAX_RECORD_NOT_EXIST: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.tax.not.exist.v2',
                        args: [
                            formatPayoutDueDate(affiliateAccount),
                            'settings?createTaxProfile=true'
                        ]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.begin.interview',
                    onClick: () => initCreateTaxProfile(history)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.TAX_PENDING_APPROVAL: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.tax.pending.v2'
                    }
                ]
            };
        }
        case Alerts.TAX_NEED_UPDATE: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.tax.need.update.v2',
                        args: [
                            formatPayoutDueDate(affiliateAccount),
                            'settings?createTaxProfile=true'
                        ]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.retake.interview.v2',
                    onClick: () => initCreateTaxProfile(history)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.TAX_INVALID: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.tax.invalid.v2',
                        args: [
                            formatPayoutDueDate(affiliateAccount),
                            'settings?createTaxProfile=true'
                        ]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.retake.interview.v2',
                    onClick: () => initCreateTaxProfile(history)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.CONSENT_RELEASE_FORM_V2: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.tax.need.consent.v2',
                        args: [formatPayoutDueDate(affiliateAccount), 'settings?isConsent=true']
                    }
                ],
                cta: {
                    code: 'epic.affiliate.common.submit.consent',
                    onClick: () => initConsent(history)
                },
                AlertPopupProps: {
                    title: 'epic.affiliate.settings.tax.profile.create.title.v2'
                }
            };
        }
        case Alerts.DECLINED_EULA_CASH:
        case Alerts.DECLINED_EULA_VBUCKS: {
            if (taxState && taxState.taxInterviewUrl) return null;
            const creatorAccountClosedAt = getAccountClosedDate(affiliateAccount);

            if (type === Alerts.DECLINED_EULA_CASH) {
                return {
                    msgCodes: [
                        {
                            code: 'epic.affiliate.disabledAccount.withPayout.v2',
                            args: [creatorAccountClosedAt]
                        },
                        {
                            code: 'epic.affiliate.common.checkUpdates'
                        }
                    ],
                    severity: 'error'
                };
            } else {
                return null;
                // return {
                //     msgCodes: [
                //         {
                //             code: 'epic.affiliate.form.account.disbale.vBucks.v1',
                //             args: [dateFormatUTC(createdAt)]
                //         },
                //         vBucks > 0 && {
                //             code: 'epic.affiliate.form.account.disbale.vBucks.finalPayment.v1',
                //             args: [
                //                 vBucks.toLocaleString('en-US', {
                //                     minimumFractionDigits: 0,
                //                     maximumFractionDigits: 0
                //                 })
                //             ]
                //         }
                //     ],
                //     severity: 'error'
                // };
            }
        }
        case Alerts.INVALID_OFFLINE_ACCOUNT: {
            return {
                msgCodes: [
                    {
                        code:
                            'epic.affiliate.settings.tax.profile.payout.offline.payment.message.v2',
                        args: [formatPayoutDueDate(affiliateAccount)]
                    }
                ],
                cta: {
                    onClick: () => initOfflineType(history),
                    code: 'epic.affiliate.settings.tax.profile.payout.offline.payment.button.v2'
                }
            };
        }
        case Alerts.BOA_ACCOUNT_NOT_EXIST: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.boa.payment.not.exist.v2',
                        args: [formatPayoutDueDate(affiliateAccount)]
                    }
                ]
            };
        }
        case Alerts.BOA_ACCOUNT_PENDING_VERIFICATION: {
            return {
                msgCodes: [
                    {
                        code:
                            'epic.affiliate.settings.tax.profile.payout.boa.payment.pending.verification.v2',
                        args: [formatPayoutDueDate(affiliateAccount)]
                    }
                ],
                cta: {
                    code: 'epic.affiliate.settings.tax.profile.active.boa.v2',
                    onClick: getBOAAccountStatusCallback(props)
                }
            };
        }
        case Alerts.BOA_ACCOUNT_PRE_ACTIVATED: {
            return {
                msgCodes: [
                    {
                        code:
                            'epic.affiliate.settings.tax.profile.payout.boa.payment.pending.activation.v2',
                        args: [formatPayoutDueDate(affiliateAccount)]
                    }
                ]
            };
        }
        case Alerts.BOA_ACCOUNT_DISABLED: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.boa.payment.disabled.v2',
                        args: [formatPayoutDueDate(affiliateAccount)]
                    }
                ]
            };
        }
        case Alerts.BOA_ACCOUNT_LOCKED: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.boa.payment.locked.v2',
                        args: [formatPayoutDueDate(affiliateAccount)]
                    }
                ]
            };
        }
        case Alerts.BOA_ACCOUNT_UNKNOWN_STATUS: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.boa.payment.unknown.v2'
                    }
                ]
            };
        }
        case Alerts.BOA_ACCOUNT_PENDING_REPROCESS: {
            return {
                msgCodes: [
                    {
                        code: 'epic.affiliate.settings.tax.profile.payout.boa.payment.reprocess.v2'
                    }
                ]
            };
        }
        default:
            return null;
    }
}
